import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { getInsightMembershipProducts } from '../services/catalog-operations.service';
import type {
  InsightMembershipCategory,
  InsightMembershipResponse,
} from '../types/catalog-operations.api.types';

/** User-selectable filter — `'ALL'` means "omit the `group` param, let the
 *  backend return everything." */
export type InsightMembershipGroupFilter = 'ALL' | 'NEWLY_ADDED' | 'CURRENTLY_SHOWN' | 'REMOVED';

/**
 * Infinite-scroll product list behind a Product Term / Occasion / Popular
 * Attribute / Macro or Micro Trend row. Page size fixed at 25. Disabled
 * until the drawer is open and a label is set, via `enabled`.
 */
export function useInsightMembershipProducts(
  workflowId: string,
  category: InsightMembershipCategory,
  label: string,
  group: InsightMembershipGroupFilter,
  enabled: boolean,
  productCategory?: string,
) {
  return useInfiniteQuery<InsightMembershipResponse>({
    queryKey: queryKeys.catalogOperations.insightMembershipProducts(
      workflowId,
      category,
      label,
      group,
      productCategory ?? '',
    ),
    queryFn: ({ pageParam }) =>
      getInsightMembershipProducts(workflowId, {
        category,
        label,
        page: pageParam as number,
        size: 25,
        ...(group === 'ALL' ? {} : { group }),
        ...(productCategory ? { productCategory } : {}),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.products.page + 1 < lastPage.products.totalPages
        ? lastPage.products.page + 1
        : undefined,
    enabled,
    // Switching the group chip changes the query key. Without this, the new
    // key starts with no data at all, so `counts` briefly disappears and the
    // whole chip row flickers out — `keepPreviousData` keeps the prior
    // group's response (same counts either way) on screen, tagged
    // `isPlaceholderData`, until the new group's page arrives.
    placeholderData: keepPreviousData,
  });
}
